import axios from 'axios';

// The two analytics reads the redesigned pages need, kept in one place so the
// library page and the per-video page send byte-identical requests to the ones
// the old `pages/members/dashboard.vue` chart and the `v-server-table` on
// `pages/videos/analytic.vue` sent. `http` is injectable for tests.

/**
 * `POST charts/dashboard` — the request the retired dashboard chart made.
 * `metric` is the API's `chart_value`: 'plays' | 'visitors' | 'finishes' | 'watches'.
 * Resolves the raw payload: `{ chart_values, selected_tab_data, has_used }`.
 */
export async function fetchChart({ metric, range, videoId, category, query }, http = axios) {
  const body = {
    chart_value: metric,
    diffDays: range.diffDays,
    fromDate: range.fromDate,
    toDate: range.toDate,
  };
  if (videoId) body.videoid = videoId;
  if (category) body.category = category;
  if (query) body.query = query;
  const response = await http.post('charts/dashboard', body);
  return response.data;
}

/**
 * `GET /analytics` with the exact params the old server table sent
 * (`range` is already the JSON string `buildRange()` produces).
 */
export async function fetchTable({ range, category, query, orderBy, ascending, limit, page }, http = axios) {
  const response = await http.get('/analytics', {
    params: {
      range: range.range,
      category,
      query,
      orderBy,
      ascending: ascending ? 1 : 0,
      limit,
      page,
    },
  });
  return { rows: response.data.data, total: response.data.meta.total };
}

/** Shapes `selected_tab_data` into the six metric cards the page shows. */
export function summaryFrom(data) {
  const d = data || {};
  const impressions = Number(d.impressions) || 0;
  const plays = Number(d.plays) || 0;
  return {
    impressions,
    uniques: Number(d.uniques) || 0,
    plays,
    finishes: Number(d.finishes) || 0,
    playRate: impressions ? Math.round((plays / impressions) * 100) : 0,
    watchTime: d.total_time_watched || '0',
    avgWatchTime: d.avg_watch_time || '0',
  };
}

/** Formats a metric for display, falling back to `'0'` for anything non-finite. */
export function formatNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString('en-US') : '0';
}

/**
 * The six stat cards both analytics pages show (library-wide on
 * `pages/analytics/index.vue`, per-video on `pages/analytics/video.vue`) —
 * same labels, notes, icons and tones either way, only the `summary` (from
 * `summaryFrom()`) differs. `format` is injectable so callers can pass their
 * own number formatter; defaults to `formatNumber`.
 */
export function buildStatCards(summary, format = formatNumber) {
  const s = summary || {};
  return [
    {
      label: 'Player loads',
      value: format(s.impressions),
      note: 'times the player appeared',
      icon: 'eye',
      tone: 'blue',
    },
    {
      label: 'Daily unique viewers',
      value: format(s.uniques),
      note: 'summed per video and reporting day',
      icon: 'users',
      tone: 'violet',
    },
    {
      label: 'Plays',
      value: format(s.plays),
      note: 'playback starts',
      icon: 'play',
      tone: 'pink',
    },
    {
      label: 'Play rate',
      value: `${s.playRate}%`,
      note: 'plays ÷ player loads',
      icon: 'chart',
      tone: 'amber',
    },
    {
      label: 'Finishes',
      value: format(s.finishes),
      note: `${s.plays ? Math.round(s.finishes / s.plays * 100) : 0}% of plays reached the end`,
      icon: 'check',
      tone: 'green',
    },
    {
      label: 'Watch time',
      value: s.watchTime,
      note: `avg ${s.avgWatchTime} per play`,
      icon: 'clock',
      tone: 'slate',
    },
  ];
}

/**
 * Merges the four per-metric chart series (each `[{x,y}]`) the per-video
 * page loads into one row per label, ordered by the first appearance of that
 * label across `plays`, `visitors`, `finishes`, `watches` in that order.
 * Missing values default to `0` so `DailyBreakdownTable` always has a full row.
 */
export function mergeSeries({ plays = [], visitors = [], finishes = [], watches = [] } = {}) {
  const series = { plays, visitors, finishes, watches };
  const order = [];
  const rows = new Map();

  Object.keys(series).forEach(key => {
    (series[key] || []).forEach(point => {
      if (!point || point.x === undefined) return;
      if (!rows.has(point.x)) {
        rows.set(point.x, { label: point.x, plays: 0, visitors: 0, finishes: 0, watches: 0 });
        order.push(point.x);
      }
      rows.get(point.x)[key] = Number(point.y) || 0;
    });
  });

  return order.map(label => rows.get(label));
}

/**
 * Stale-response guard. One `latestOnly()` per loader; call the returned
 * function to take a ticket before the await, then call *its* return value
 * after it — it is `false` when a newer request has since been started, so the
 * latest request always wins on rapid preset / metric / search changes.
 *
 *   const guard = latestOnly();
 *   const done = guard();
 *   const data = await fetchChart(…);
 *   if (!done()) return;
 */
export function latestOnly() {
  let ticket = 0;
  return function take() {
    const mine = ticket + 1;
    ticket = mine;
    return function isLatest() {
      return mine === ticket;
    };
  };
}

export default {
  fetchChart,
  fetchTable,
  summaryFrom,
  latestOnly,
  mergeSeries,
  formatNumber,
  buildStatCards,
};

export function downloadCsv(filename, rows) {
  const cell = value => {
    let text = String(value == null ? '' : value);
    if (/^[=+@\-\t\r]/.test(text)) text = "'" + text;
    return '"' + text.replace(/"/g, '""') + '"';
  };
  const url = URL.createObjectURL(new Blob(['\ufeff' + rows.map(row => row.map(cell).join(',')).join('\r\n')], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url; link.download = filename; link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
